import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "rpc_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Once a session-expiry redirect has been triggered, don't fire it again for the burst of other
// in-flight requests that also 401 at the same moment (e.g. cases + progress + bookmarks all
// loading together) — avoids stacking duplicate toasts/redirects.
let sessionExpiredHandled = false;

client.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    // Only a request that WAS sending a token (i.e. the user believed they were logged in)
    // should trigger a forced re-login. A bare /auth/login attempt with wrong credentials is
    // also a 401 but never carries an Authorization header — that must keep surfacing its own
    // "invalid email or password" message on the login form, not bounce back to /login.
    const hadToken = !!error?.config?.headers?.Authorization;
    if (status === 401 && hadToken && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      setToken(null);
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        toast.error("Your session has expired. Please sign in again.");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

function unwrapError(e) {
  const detail = e?.response?.data?.detail || e?.response?.data?.message;
  const status = e?.response?.status;
  const msg = typeof detail === "string" ? detail
    : (detail && typeof detail === "object" && typeof detail.message === "string") ? detail.message
    : (detail ? JSON.stringify(detail) : e.message || "Request failed");
  const err = new Error(msg);
  err.status = status;
  err.detail = detail;
  return err;
}

async function req(method, path, body, opts = {}) {
  try {
    const res = await client.request({ method, url: path, data: body, ...opts });
    return res.data;
  } catch (e) {
    throw unwrapError(e);
  }
}

export const api = {
  // config
  config: () => req("get", "/config"),
  // auth
  register: (b) => req("post", "/auth/register", b),
  verify: (b) => req("post", "/auth/verify", b),
  resendCode: (email) => req("post", "/auth/resend-code", { email }),
  login: (b) => req("post", "/auth/login", b),
  googleSession: (session_id) => req("post", "/auth/google/session", { session_id }),
  forgot: (email) => req("post", "/auth/forgot-password", { email }),
  reset: (b) => req("post", "/auth/reset-password", b),
  logoutEverywhere: () => req("post", "/auth/logout-everywhere", {}),
  me: () => req("get", "/auth/me"),
  updateProfile: (b) => req("put", "/profile", b),
  deleteAccount: () => req("delete", "/account"),
  // plans / pay
  plans: () => req("get", "/plans"),
  validatePromo: (b) => req("post", "/promo/validate", b),
  checkoutQuote: (params) => req("get", "/checkout/quote", undefined, { params }),
  checkout: (b) => req("post", "/checkout/session", b),
  checkoutStatus: (id) => req("get", `/checkout/status/${id}`),
  cancelRenewal: () => req("post", "/billing/cancel-renewal", {}),
  billingSummary: () => req("get", "/billing/summary"),
  // credits (non-legacy / non-actively-subscribed billing)
  creditsBalance: () => req("get", "/credits/balance"),
  creditsLedger: (params = {}) => req("get", "/credits/ledger", undefined, { params }),
  creditsCheckout: (b) => req("post", "/credits/checkout", b),
  creditsCheckoutStatus: (sessionId) => req("get", `/credits/checkout/${sessionId}`),
  failedCreditSessions: () => req("get", "/credits/sessions/failed"),
  reportSessionFailure: (sessionId, b) => req("post", `/ai/session/${sessionId}/report-failure`, b || {}),
  refundSessionCredit: (sessionId) => req("post", `/credits/sessions/${sessionId}/refund`, {}),
  refundMockExamCredit: (mockId) => req("post", `/credits/mock-exams/${mockId}/refund`, {}),
  adminUserCreditsLedger: (userId) => req("get", `/admin/users/${userId}/credits/ledger`),
  adminAdjustUserCredits: (userId, b) => req("post", `/admin/users/${userId}/credits/adjust`, b),
  adminFailedCreditSessions: () => req("get", "/admin/credits/failed-sessions"),
  // cases
  cases: (params = {}) => req("get", "/cases", undefined, { params }),
  randomCase: (params = {}) => req("get", "/cases/random", undefined, { params }),
  caseById: (id) => req("get", `/cases/${id}`),
  stationBrief: (id) => req("get", `/cases/${id}/station`),
  myCustomCases: () => req("get", "/cases/custom/mine"),
  generateCustomCase: (b) => req("post", "/cases/custom/generate", b),
  saveCustomCase: (b) => req("post", "/cases/custom", b),
  deleteCustomCase: (id) => req("delete", `/cases/custom/${id}`),
  // ai
  aiStart: (b) => req("post", "/ai/start", b),
  aiText: (b) => req("post", "/ai/text-turn", b),
  aiTranscribe: (b) => req("post", "/ai/transcribe", b),
  aiVoice: (b) => req("post", "/ai/voice-turn", b),
  aiSpeak: (b) => req("post", "/ai/speak", b),
  aiFeedback: (b) => req("post", "/ai/feedback", b),
  recheckKeyStep: (session_id, step_index) => req("post", "/ai/feedback/recheck-step", { session_id, step_index }),
  sessions: () => req("get", "/sessions"),
  sessionById: (id) => req("get", `/sessions/${id}`),
  progress: () => req("get", "/progress"),
  bookmarks: () => req("get", "/bookmarks"),
  addBookmark: (session_id) => req("post", "/bookmarks", { session_id }),
  removeBookmark: (id) => req("delete", `/bookmarks/${id}`),
  bookmarkStatus: (session_id) => req("get", `/bookmarks/status/${session_id}`),
  // case bookmarks (save a whole case for quick access)
  caseBookmarks: () => req("get", "/case-bookmarks"),
  caseBookmarkIds: () => req("get", "/case-bookmarks/ids"),
  toggleCaseBookmark: (case_id) => req("post", "/case-bookmarks", { case_id }),
  removeCaseBookmark: (case_id) => req("delete", `/case-bookmarks/${case_id}`),
  // notebook (personal notes from feedback reports)
  notebook: (q) => req("get", "/notebook", undefined, { params: q ? { q } : {} }),
  addNote: (b) => req("post", "/notebook", b),
  updateNote: (id, text) => req("put", `/notebook/${id}`, { text }),
  deleteNote: (id) => req("delete", `/notebook/${id}`),
  // reset practice history (destructive)
  resetPractice: (b) => req("post", "/practice/reset", b),
  // mock exam
  mockStart: () => req("post", "/mock-exam/start"),
  mockActive: () => req("get", "/mock-exam/active"),
  mockGet: (id) => req("get", `/mock-exam/${id}`),
  mockScoreSlot: (id, idx) => req("post", `/mock-exam/${id}/slot/${idx}/score`),
  mockFinish: (id) => req("post", `/mock-exam/${id}/finish`),
  mockAbandon: (id) => req("post", `/mock-exam/${id}/abandon`),
  mockList: () => req("get", "/mock-exams"),
  exportRevisionPdf: async () => {
    const res = await client.get(`/bookmarks/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "revision-sheet.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },
  exportBookmarkPdf: async (id, title = "revision") => {
    const res = await client.get(`/bookmarks/${id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    const safe = (title || "revision").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 50);
    a.download = `revision-${safe}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },
  // connect
  setAvailability: (available) => req("put", "/connect/availability", { available }),
  partners: () => req("get", "/connect/partners"),
  createRoom: () => req("post", "/connect/room", {}),
  connectSchedule: () => req("get", "/connect/schedule"),
  queueJoin: () => req("post", "/connect/queue/join", {}),
  queueLeave: () => req("post", "/connect/queue/leave", {}),
  queueStatus: () => req("get", "/connect/queue/status"),
  roomActive: () => req("get", "/connect/room/active"),
  roomRejoin: (matchId) => req("get", `/connect/room/${matchId}`),
  roomLeave: (matchId) => req("post", "/connect/room/leave", { match_id: matchId, rating: 0 }),
  rateMatch: (b) => req("post", "/connect/rate", b),
  // private practice rooms (host-approved, max 3)
  roomsList: () => req("get", "/connect/rooms"),
  roomCreate: (title) => req("post", "/connect/rooms", { title }),
  roomDetail: (id) => req("get", `/connect/rooms/${id}`),
  roomRequestJoin: (id) => req("post", `/connect/rooms/${id}/request`, {}),
  roomApprove: (id, reqId) => req("post", `/connect/rooms/${id}/requests/${reqId}/approve`, {}),
  roomDeny: (id, reqId) => req("post", `/connect/rooms/${id}/requests/${reqId}/deny`, {}),
  roomLeavePrivate: (id) => req("post", `/connect/rooms/${id}/leave`, {}),
  // in-call attachment sharing (images + files) + text chat
  uploadAttachment: (b) => req("post", "/connect/chat/attachment", b),
  chatAttachments: (channel) => req("get", `/connect/chat/${channel}/attachments`),
  chatTimeline: (channel) => req("get", `/connect/chat/${channel}/timeline`),
  sendChatMessage: (channel, text) => req("post", `/connect/chat/${channel}/message`, { text }),
  // friends / social
  presencePing: () => req("post", "/presence/ping", {}),
  friends: () => req("get", "/friends"),
  friendRequests: () => req("get", "/friends/requests"),
  sendFriendRequest: (toId) => req("post", "/friends/request", { to_id: toId }),
  acceptFriendRequest: (id) => req("post", `/friends/requests/${id}/accept`, {}),
  declineFriendRequest: (id) => req("post", `/friends/requests/${id}/decline`, {}),
  removeFriend: (id) => req("delete", `/friends/${id}`),
  searchUsers: (q) => req("get", "/friends/search", undefined, { params: { q } }),
  socialNotifications: () => req("get", "/social/notifications"),
  matchPeople: (matchId) => req("get", `/connect/match/${matchId}/people`),
  inviteFriendToRoom: (friendId, title) => req("post", "/connect/rooms/invite", { friend_id: friendId, title }),
  roomInvites: () => req("get", "/connect/invites"),
  acceptRoomInvite: (id) => req("post", `/connect/invites/${id}/accept`, {}),
  declineRoomInvite: (id) => req("post", `/connect/invites/${id}/decline`, {}),
  // admin
  adminStats: () => req("get", "/admin/stats"),
  adminPracticeStats: () => req("get", "/admin/stats/practice"),
  adminDeepgramStatus: () => req("get", "/admin/deepgram/status"),
  adminUsers: () => req("get", "/admin/users"),
  launchNoticeStatus: () => req("get", "/admin/launch-notice/status"),
  launchNoticeSave: (b) => req("put", "/admin/launch-notice/save", b),
  launchNoticePreview: (b) => req("post", "/admin/launch-notice/preview", b || {}),
  launchNoticeSendNow: (b) => req("post", "/admin/launch-notice/send-now", b || {}),
  launchNoticeReset: () => req("post", "/admin/launch-notice/reset", {}),
  launchNoticeResetTemplate: () => req("post", "/admin/launch-notice/reset-template", {}),
  updateNoticeStatus: () => req("get", "/admin/update-notice/status"),
  updateNoticePreview: () => req("post", "/admin/update-notice/preview", {}),
  updateNoticeTest: () => req("post", "/admin/update-notice/test", {}),
  updateNoticeSendNow: () => req("post", "/admin/update-notice/send-now", {}),
  updateNoticeReset: () => req("post", "/admin/update-notice/reset", {}),
  featureUpdateStatus: () => req("get", "/admin/feature-update/status"),
  featureUpdatePreview: () => req("post", "/admin/feature-update/preview", {}),
  featureUpdateTest: () => req("post", "/admin/feature-update/test", {}),
  featureUpdateSendNow: () => req("post", "/admin/feature-update/send-now", {}),
  featureUpdateReset: () => req("post", "/admin/feature-update/reset", {}),
  realtimeUpdateStatus: () => req("get", "/admin/realtime-update/status"),
  realtimeUpdateSave: (b) => req("put", "/admin/realtime-update/save", b || {}),
  realtimeUpdatePreview: (b) => req("post", "/admin/realtime-update/preview", b || {}),
  realtimeUpdateResetTemplate: () => req("post", "/admin/realtime-update/reset-template", {}),
  realtimeUpdateTest: (b) => req("post", "/admin/realtime-update/test", b || {}),
  realtimeUpdateSendNow: (b) => req("post", "/admin/realtime-update/send-now", b || {}),
  realtimeUpdateReset: () => req("post", "/admin/realtime-update/reset", {}),
  casesUpdateStatus: () => req("get", "/admin/cases-update/status"),
  casesUpdateSave: (b) => req("put", "/admin/cases-update/save", b || {}),
  casesUpdatePreview: (b) => req("post", "/admin/cases-update/preview", b || {}),
  casesUpdateResetTemplate: () => req("post", "/admin/cases-update/reset-template", {}),
  casesUpdateTest: (b) => req("post", "/admin/cases-update/test", b || {}),
  casesUpdateSendNow: (b) => req("post", "/admin/cases-update/send-now", b || {}),
  casesUpdateReset: () => req("post", "/admin/cases-update/reset", {}),
  customUpdateStatus: () => req("get", "/admin/custom-update/status"),
  customUpdateSave: (b) => req("put", "/admin/custom-update/save", b || {}),
  customUpdatePreview: (b) => req("post", "/admin/custom-update/preview", b || {}),
  customUpdateResetTemplate: () => req("post", "/admin/custom-update/reset-template", {}),
  customUpdateTest: (b) => req("post", "/admin/custom-update/test", b || {}),
  customUpdateSendNow: (b) => req("post", "/admin/custom-update/send-now", b || {}),
  customUpdateReset: () => req("post", "/admin/custom-update/reset", {}),
  streakStatus: () => req("get", "/admin/streak-reminders/status"),
  streakArm: (armed) => req("put", "/admin/streak-reminders/arm", { armed }),
  streakRun: () => req("post", "/admin/streak-reminders/run", {}),
  grantSubscription: (userId, b) => req("post", `/admin/users/${userId}/grant-subscription`, b),
  adminTxns: () => req("get", "/admin/transactions"),
  adminCommissions: () => req("get", "/admin/commissions"),
  toggleCommission: (id) => req("put", `/admin/commissions/${id}/toggle`, {}),
  adminPayouts: () => req("get", "/admin/payouts"),
  markProviderPaid: (pid) => req("post", `/admin/payouts/${pid}/mark-paid`, {}),
  adminPayoutsMonthly: () => req("get", "/admin/payouts/monthly"),
  markProviderMonthPaid: (pid, month, extra = {}) => req("post", `/admin/payouts/${pid}/mark-month-paid`, { month, ...extra }),
  downloadPayoutStatement: async (pid, month, providerName) => {
    const res = await client.get(`/admin/payouts/${pid}/statement`, { params: { month }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    const safe = (providerName || "provider").replace(/[^A-Za-z0-9_-]+/g, "_");
    a.download = `payout-${safe}-${month}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  getPayoutReceipt: async (receiptId, { download = false, providerName = "provider", month = "" } = {}) => {
    const res = await client.get(`/admin/payouts/receipt/${receiptId}`, { responseType: "blob" });
    const type = res.data?.type || "image/png";
    const blob = new Blob([res.data], { type });
    const url = window.URL.createObjectURL(blob);
    if (download) {
      const a = document.createElement("a");
      a.href = url;
      const safe = (providerName || "provider").replace(/[^A-Za-z0-9_-]+/g, "_");
      const ext = (type.split("/")[1] || "png").split("+")[0];
      a.download = `bank-transfer-${safe}-${month}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    }
  },
  adminPromos: () => req("get", "/admin/promos"),
  createPromo: (b) => req("post", "/admin/promos", b),
  togglePromo: (id) => req("put", `/admin/promos/${id}/toggle`, {}),
  adminProviders: () => req("get", "/admin/providers"),
  createProvider: (b) => req("post", "/admin/providers", b),
  updateProviderEmail: (id, email) => req("put", `/admin/providers/${id}/email`, { email }),
  adminMatchingStatus: () => req("get", "/admin/matching"),
  adminMatchingToggle: (open) => req("put", "/admin/matching", { open }),
  adminRemindersStatus: () => req("get", "/admin/reminders"),
  adminRunReminders: () => req("post", "/admin/reminders/run", {}),
  adminPowerHours: () => req("get", "/admin/power-hours"),
  adminUpdatePowerHours: (windows) => req("put", "/admin/power-hours", { windows }),
  adminLaunchStatus: () => req("get", "/admin/launch"),
  adminLaunchToggle: (active) => req("put", "/admin/launch", { active }),
  createCase: (b) => req("post", "/admin/cases", b),
  deleteCase: (id) => req("delete", `/admin/cases/${id}`),
  // partner
  partnerDashboard: () => req("get", "/partner/dashboard"),
  downloadPartnerStatement: async (month, providerName) => {
    const res = await client.get(`/partner/statement`, { params: { month }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    const safe = (providerName || "provider").replace(/[^A-Za-z0-9_-]+/g, "_");
    a.download = `statement-${safe}-${month}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// ---------------------------------------------------------------------------
// SSE token streaming for the live AI role-play turn.
// Uses fetch (not axios) so we can read the response body incrementally and
// render the AI reply token-by-token. Calls:
//   onSpeaker(speaker)   once, when the patient/examiner is resolved
//   onDelta(textChunk)   repeatedly, as tokens arrive
// Resolves with { speaker, reply_text } on completion; rejects on error.
// ---------------------------------------------------------------------------
export async function streamTurn({ session_id, text }, { onSpeaker, onDelta } = {}) {
  const token = getToken();
  const res = await fetch(`${API}/ai/text-turn/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ session_id, text }),
  });
  if (!res.ok || !res.body) {
    let detail = "Could not send";
    try { const j = await res.json(); detail = j.detail || detail; } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let speaker = "patient";
  let full = "";

  const handleEvent = (block) => {
    // Each SSE event may contain one or more `data: ...` lines.
    const dataLines = block
      .split("\n")
      .filter((l) => l.startsWith("data:"))
      .map((l) => l.slice(5).trim());
    if (!dataLines.length) return;
    let payload;
    try { payload = JSON.parse(dataLines.join("")); } catch { return; }
    if (payload.error) {
      const err = new Error(payload.error);
      err.status = payload.status;
      throw err;
    }
    if (payload.speaker && !payload.done) {
      speaker = payload.speaker;
      onSpeaker && onSpeaker(speaker);
    }
    if (typeof payload.delta === "string") {
      full += payload.delta;
      onDelta && onDelta(payload.delta);
    }
    if (payload.done) {
      if (payload.speaker) speaker = payload.speaker;
      if (typeof payload.reply_text === "string") full = payload.reply_text;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      handleEvent(block);
    }
  }
  if (buf.trim()) handleEvent(buf);

  return { speaker, reply_text: full.trim() };
}
